import { GRIP_PAIN_NOTE, GRIP_EFFORT_NOTE } from '../utils/gripValidity';
import React, { useMemo, useState, useCallback } from 'react';
import { unratedSensory, incompleteGrip, gripMotionConflict } from '../utils/unratedSensory';
import { ratingSummary } from '../utils/ratingSummary';
import { ratedParts, combinedTotal, combinedTotalNarrative } from '../utils/combinedTotal';
import { TKA, THA, replacementRating } from '../utils/jointReplacement';
import { handSideRecorded } from '../utils/examData';
import { omitUnrecorded } from '../utils/omitUnrecorded';
import { combine, combineAMAValues } from '../utils/amaMath';
import { HAND_IMPAIRMENT } from '../utils/handCalculations';
import { copyReportToClipboard } from '../utils/reportClipboard';
import {
  calculateFlexion,
  calculateExtension,
  calculateAbduction,
  calculateAdduction,
  calculateInternalRotation,
  calculateExternalRotation,
  calculateWristFlexion,
  calculateWristExtension,
  calculateWristRadialDeviation,
  calculateWristUlnarDeviation,
  calculateElbowFlexion,
  calculateElbowExtension,
  calculatePronation,
  calculateSupination,
  calculateHipFlexion,
  calculateHipExtensionContracture,
  calculateHipInternalRotation,
  calculateHipExternalRotation,
  calculateHipAdduction,
  calculateHipAbductionContracture,
  calculateKneeFlexion,
  calculateKneeFlexionContracture,
  calculateKneeVarus,
  calculateKneeValgus,
  calculateAnklePlantarFlexion,
  calculateAnkleDorsiflexion,
  calculateAnkleInversion,
  calculateAnkleEversion,
} from './TabRatingNarratives';

interface ResultsSummaryBoxProps {
  examData: any | null;
  darkMode: boolean;
}

/**
 * A component that displays a summary of all exam results in one box
 * with a copy button that copies the content in Times New Roman font.
 * Uses the same narrative logic as the components at the bottom of each tab.
 */
const ResultsSummaryBox: React.FC<ResultsSummaryBoxProps> = ({ examData, darkMode }) => {
  const [copied, setCopied] = useState(false);
  
  // Remove any HTML tags from text for clipboard
  const stripHtmlTags = useCallback((text: string): string => {
    return text.replace(/<[^>]*>/g, '');
  }, []);

  const [copyFailed, setCopyFailed] = useState(false);
  
  // Utility functions
  // Utility functions removed - were unused
  
  // Helper to check if any inputs exist in an object
  const hasAnyInputs = (obj: any): boolean => {
    if (!obj) return false;
    return Object.values(obj).some(val => {
      if (typeof val === 'number') return !isNaN(val);
      if (typeof val === 'object' && val !== null) return hasAnyInputs(val);
      return false;
    });
  };
  
  // Helper function to check if grip data has inputs for both sides
  const hasGripInputs = (gripData: any): boolean => {
    if (!gripData) return false;
    
    // Check if we have at least one measurement for right side
    const hasRightGrip = (
      (gripData.right_attempt_1 !== undefined && gripData.right_attempt_1 !== null) ||
      (gripData.right_attempt_2 !== undefined && gripData.right_attempt_2 !== null) ||
      (gripData.right_attempt_3 !== undefined && gripData.right_attempt_3 !== null)
    );
    
    // Check if we have at least one measurement for left side
    const hasLeftGrip = (
      (gripData.left_attempt_1 !== undefined && gripData.left_attempt_1 !== null) ||
      (gripData.left_attempt_2 !== undefined && gripData.left_attempt_2 !== null) ||
      (gripData.left_attempt_3 !== undefined && gripData.left_attempt_3 !== null)
    );
    
    // We need at least one measurement for each side to compare and calculate impairment
    return hasRightGrip && hasLeftGrip;
  };
  
  // Helper function to check if finger data has inputs for a specific side
  const hasFingerInputs = useCallback((fingerData: any, side: string): boolean => {
    if (!fingerData) return false;
    
    // Generic check for any numeric or string properties for the given side
    return Object.keys(fingerData).some(key => {
      const val = fingerData[key];
      return key.startsWith(side) && 
             ((typeof val === 'number' && !isNaN(val)) ||
              (typeof val === 'string' && val !== null && val !== '' && val !== undefined));
    });
  }, []);
  
  // Helper function to check if hand data has inputs
  const hasHandInputs = useCallback((handData: any): boolean => {
    if (!handData) return false;
    
    // Check each finger section
    const hasThumbInputs = handData.thumbs && (
      hasFingerInputs(handData.thumbs, 'right') || 
      hasFingerInputs(handData.thumbs, 'left')
    );
    
    const hasIndexInputs = handData.index_fingers && (
      hasFingerInputs(handData.index_fingers, 'right') || 
      hasFingerInputs(handData.index_fingers, 'left')
    );
    
    const hasMiddleInputs = handData.middle_fingers && (
      hasFingerInputs(handData.middle_fingers, 'right') || 
      hasFingerInputs(handData.middle_fingers, 'left')
    );
    
    const hasRingInputs = handData.ring_fingers && (
      hasFingerInputs(handData.ring_fingers, 'right') || 
      hasFingerInputs(handData.ring_fingers, 'left')
    );
    
    const hasLittleInputs = handData.little_fingers && (
      hasFingerInputs(handData.little_fingers, 'right') || 
      hasFingerInputs(handData.little_fingers, 'left')
    );
    
    return hasThumbInputs || hasIndexInputs || hasMiddleInputs || hasRingInputs || hasLittleInputs;
  }, [hasFingerInputs]);
  
  // Helper functions for other body regions
  const hasShoulderInputs = (shoulderData: any): boolean => {
    if (!shoulderData) return false;
    return shoulderData.right_flexion !== null || shoulderData.right_extension !== null ||
           shoulderData.right_abduction !== null || shoulderData.right_adduction !== null ||
           shoulderData.right_internal_rotation !== null || shoulderData.right_external_rotation !== null ||
           shoulderData.left_flexion !== null || shoulderData.left_extension !== null ||
           shoulderData.left_abduction !== null || shoulderData.left_adduction !== null ||
           shoulderData.left_internal_rotation !== null || shoulderData.left_external_rotation !== null;
  };
  
  const hasElbowInputs = (elbowData: any): boolean => {
    if (!elbowData) return false;
    return hasAnyInputs(elbowData);
  };
  
  const hasWristInputs = (examData: any): boolean => {
    if (!examData.wrists) return false;
    
    return examData.wrists.right_flexion !== null ||
           examData.wrists.right_extension !== null ||
           examData.wrists.right_radial_deviation !== null ||
           examData.wrists.right_ulnar_deviation !== null ||
           examData.wrists.left_flexion !== null ||
           examData.wrists.left_extension !== null ||
           examData.wrists.left_radial_deviation !== null ||
           examData.wrists.left_ulnar_deviation !== null;
  };

  const hasHipInputs = (hipData: any): boolean => {
    if (!hipData) return false;
    
    return hipData.right_flexion !== null ||
           hipData.right_extension_contracture !== null ||
           hipData.right_abduction_contracture !== null ||
           hipData.right_adduction !== null ||
           hipData.right_internal_rotation !== null ||
           hipData.right_external_rotation !== null ||
           hipData.left_flexion !== null ||
           hipData.left_extension_contracture !== null ||
           hipData.left_abduction_contracture !== null ||
           hipData.left_adduction !== null ||
           hipData.left_internal_rotation !== null ||
           hipData.left_external_rotation !== null;
  };
  
  const hasKneeInputs = (kneeData: any): boolean => {
    if (!kneeData) return false;
    
    return kneeData.right_flexion !== null ||
           kneeData.right_flexion_contracture_passive !== null ||
           kneeData.right_varus !== null ||
           kneeData.right_valgus !== null ||
           kneeData.left_flexion !== null ||
           kneeData.left_flexion_contracture_passive !== null ||
           kneeData.left_varus !== null ||
           kneeData.left_valgus !== null;
  };
  
  const hasAnkleInputs = (ankleData: any): boolean => {
    if (!ankleData) return false;
    
    return ankleData.right_plantar_flexion !== null ||
           ankleData.right_dorsiflexion !== null ||
           ankleData.right_inversion !== null ||
           ankleData.right_eversion !== null ||
           ankleData.left_plantar_flexion !== null ||
           ankleData.left_dorsiflexion !== null ||
           ankleData.left_inversion !== null ||
           ankleData.left_eversion !== null;
  };
  
  // combine() and combineAMAValues() imported from utils/amaMath
  // (shared single source of truth for AMA Combined Values math)

  
  // Utility function to combine two impairment values (replaced by n8n version below)
  
  


  
  
  
  
  
  

  // Index finger calculation functions - exact n8n logic
  const calcDIPFlex = HAND_IMPAIRMENT.dipFlex;

  const calcDIPExt = HAND_IMPAIRMENT.dipExt;

  const calcPIPFlex = HAND_IMPAIRMENT.pipFlex;

  const calcPIPExt = HAND_IMPAIRMENT.pipExt;

  const calcMCPFlex = HAND_IMPAIRMENT.mcpFlex;

  const calcMCPExt = HAND_IMPAIRMENT.mcpExt;


  // Ring finger functions (same anchor points as index and middle fingers - exact n8n logic)
  function calcRingDIPFlexion(val: number | null): number { return calcDIPFlex(val); }
  function calcRingDIPExtension(val: number | null): number { return calcDIPExt(val); }
  function calcRingPIPFlexion(val: number | null): number { return calcPIPFlex(val); }
  function calcRingPIPExtension(val: number | null): number { return calcPIPExt(val); }
  function calcRingMCPFlexion(val: number | null): number { return calcMCPFlex(val); }
  function calcRingMCPExtension(val: number | null): number { return calcMCPExt(val); }

  // Small finger functions (same anchor points as index, middle, and ring fingers - exact n8n logic)
  function calcSmallDIPFlexion(val: number | null): number { return calcDIPFlex(val); }
  function calcSmallDIPExtension(val: number | null): number { return calcDIPExt(val); }
  function calcSmallPIPFlexion(val: number | null): number { return calcPIPFlex(val); }
  function calcSmallPIPExtension(val: number | null): number { return calcPIPExt(val); }
  function calcSmallMCPFlexion(val: number | null): number { return calcMCPFlex(val); }
  function calcSmallMCPExtension(val: number | null): number { return calcMCPExt(val); }



  


  // Helper function for triggering severity calculation (removed duplicate)
  
  
  
  
  
  
  
  
  
  
  
  
  
  
  
  




  const calcExtensorSubluxationSeverity = (subluxation: string | null): number => {
    if (!subluxation) return 0;
    const sub = subluxation.toLowerCase().trim();
    if (sub.includes("none")) return 0;
    if (sub.includes("mild")) return 10;
    if (sub.includes("moderate")) return 20;
    if (sub.includes("severe")) return 30;
    if (sub.startsWith("n")) return 0;
    if (sub.startsWith("mi")) return 10;
    if (sub.startsWith("mod")) return 20;
    if (sub.startsWith("s")) return 30;
    return 0;
  };
  

  // Generate comprehensive hand narrative matching n8n structure exactly
  const generateHandNarrative = useCallback((handData: any, side: 'right' | 'left'): string | null => {
    if (!handData) return null;
    // A side with nothing recorded wasn't examined: say nothing about it, as
    // every other region does (it used to print "LEFT HAND: No impairment.").
    if (!handSideRecorded(handData, side)) return null;

    const isRight = side === 'right';
    const normalizedSide = side + ' hand';
    const capitalizedSide = normalizedSide.charAt(0).toUpperCase() + normalizedSide.slice(1);

    // combine()/combineAMAValues() imported from utils/amaMath

    // ===================================
    // DATA SETUP FOR EACH DIGIT
    // ===================================

    const thumbs = handData.thumbs || {};
    const thumbData = {
      ipFlexion: isRight ? thumbs.right_ip_flexion : thumbs.left_ip_flexion,
      ipExtension: isRight ? thumbs.right_ip_extension : thumbs.left_ip_extension,
      mcpFlexion: isRight ? thumbs.right_mcp_flexion : thumbs.left_mcp_flexion,
      mcpExtension: isRight ? thumbs.right_mcp_extension : thumbs.left_mcp_extension,
      cmcRadialAbduction: isRight ? thumbs.right_cmc_radial_abduction : thumbs.left_cmc_radial_abduction,
      cmcAdduction: isRight ? thumbs.right_cmc_adduction : thumbs.left_cmc_adduction,
      cmcOpposition: isRight ? thumbs.right_cmc_opposition : thumbs.left_cmc_opposition,
      radialSensLevel: isRight ? parseFloat(thumbs.right_radial_two_point) || 0 : parseFloat(thumbs.left_radial_two_point) || 0,
      radialSensPercent: isRight ? parseFloat(thumbs.right_radial_two_point_percent) || 0 : parseFloat(thumbs.left_radial_two_point_percent) || 0,
      ulnarSensLevel: isRight ? parseFloat(thumbs.right_ulnar_two_point) || 0 : parseFloat(thumbs.left_ulnar_two_point) || 0,
      ulnarSensPercent: isRight ? parseFloat(thumbs.right_ulnar_two_point_percent) || 0 : parseFloat(thumbs.left_ulnar_two_point_percent) || 0,
      amputationPercent: isRight ? (thumbs.right_amputation_percent || 0) : (thumbs.left_amputation_percent || 0),
      digitWorth: 40,
      triggering: isRight ? (thumbs.right_triggering || "") : (thumbs.left_triggering || ""),
    };

    const indexFingers = handData.index_fingers || {};
    const indexData = {
      dipFlexion: isRight ? indexFingers.right_dip_flexion : indexFingers.left_dip_flexion,
      dipExtension: isRight ? indexFingers.right_dip_extension : indexFingers.left_dip_extension,
      pipFlexion: isRight ? indexFingers.right_pip_flexion : indexFingers.left_pip_flexion,
      pipExtension: isRight ? indexFingers.right_pip_extension : indexFingers.left_pip_extension,
      mcpFlexion: isRight ? indexFingers.right_mcp_flexion : indexFingers.left_mcp_flexion,
      mcpExtension: isRight ? indexFingers.right_mcp_extension : indexFingers.left_mcp_extension,
      radialSensLevel: isRight ? indexFingers.right_radial_two_point : indexFingers.left_radial_two_point,
      radialSensPercent: isRight ? indexFingers.right_radial_two_point_percent : indexFingers.left_radial_two_point_percent,
      ulnarSensLevel: isRight ? indexFingers.right_ulnar_two_point : indexFingers.left_ulnar_two_point,
      ulnarSensPercent: isRight ? indexFingers.right_ulnar_two_point_percent : indexFingers.left_ulnar_two_point_percent,
      amputationPercent: isRight ? (indexFingers.right_amputation_percent || 0) : (indexFingers.left_amputation_percent || 0),
      triggering: isRight ? (indexFingers.right_triggering || "") : (indexFingers.left_triggering || ""),
      extensorSubluxation: isRight ? (indexFingers.right_extensor_subluxation_mcp || "") : (indexFingers.left_extensor_subluxation_mcp || ""),
      digitWorth: 20,
    };

    const middleFingers = handData.middle_fingers || {};
    const middleData = {
      dipFlexion: isRight ? middleFingers.right_dip_flexion : middleFingers.left_dip_flexion,
      dipExtension: isRight ? middleFingers.right_dip_extension : middleFingers.left_dip_extension,
      pipFlexion: isRight ? middleFingers.right_pip_flexion : middleFingers.left_pip_flexion,
      pipExtension: isRight ? middleFingers.right_pip_extension : middleFingers.left_pip_extension,
      mcpFlexion: isRight ? middleFingers.right_mcp_flexion : middleFingers.left_mcp_flexion,
      mcpExtension: isRight ? middleFingers.right_mcp_extension : middleFingers.left_mcp_extension,
      radialSensLevel: isRight ? middleFingers.right_radial_two_point : middleFingers.left_radial_two_point,
      radialSensPercent: isRight ? middleFingers.right_radial_two_point_percent : middleFingers.left_radial_two_point_percent,
      ulnarSensLevel: isRight ? middleFingers.right_ulnar_two_point : middleFingers.left_ulnar_two_point,
      ulnarSensPercent: isRight ? middleFingers.right_ulnar_two_point_percent : middleFingers.left_ulnar_two_point_percent,
      amputationPercent: isRight ? (middleFingers.right_amputation_percent || 0) : (middleFingers.left_amputation_percent || 0),
      triggering: isRight ? (middleFingers.right_triggering || "") : (middleFingers.left_triggering || ""),
      extensorSubluxation: isRight ? (middleFingers.right_extensor_subluxation_mcp || "") : (middleFingers.left_extensor_subluxation_mcp || ""),
      digitWorth: 20,
    };

    const ringFingers = handData.ring_fingers || {};
    const ringData = {
      dipFlexion: isRight ? ringFingers.right_dip_flexion : ringFingers.left_dip_flexion,
      dipExtension: isRight ? ringFingers.right_dip_extension : ringFingers.left_dip_extension,
      pipFlexion: isRight ? ringFingers.right_pip_flexion : ringFingers.left_pip_flexion,
      pipExtension: isRight ? ringFingers.right_pip_extension : ringFingers.left_pip_extension,
      mcpFlexion: isRight ? ringFingers.right_mcp_flexion : ringFingers.left_mcp_flexion,
      mcpExtension: isRight ? ringFingers.right_mcp_extension : ringFingers.left_mcp_extension,
      radialSensLevel: isRight ? ringFingers.right_radial_two_point : ringFingers.left_radial_two_point,
      radialSensPercent: isRight ? ringFingers.right_radial_two_point_percent : ringFingers.left_radial_two_point_percent,
      ulnarSensLevel: isRight ? ringFingers.right_ulnar_two_point : ringFingers.left_ulnar_two_point,
      ulnarSensPercent: isRight ? ringFingers.right_ulnar_two_point_percent : ringFingers.left_ulnar_two_point_percent,
      amputationPercent: isRight ? (ringFingers.right_amputation_percent || 0) : (ringFingers.left_amputation_percent || 0),
      triggering: isRight ? (ringFingers.right_triggering || "") : (ringFingers.left_triggering || ""),
      extensorSubluxation: isRight ? (ringFingers.right_extensor_subluxation_mcp || "") : (ringFingers.left_extensor_subluxation_mcp || ""),
      digitWorth: 10,
    };

    const smallFingers = handData.little_fingers || handData.small_fingers || {};
    const smallData = {
      dipFlexion: isRight ? smallFingers.right_dip_flexion : smallFingers.left_dip_flexion,
      dipExtension: isRight ? smallFingers.right_dip_extension : smallFingers.left_dip_extension,
      pipFlexion: isRight ? smallFingers.right_pip_flexion : smallFingers.left_pip_flexion,
      pipExtension: isRight ? smallFingers.right_pip_extension : smallFingers.left_pip_extension,
      mcpFlexion: isRight ? smallFingers.right_mcp_flexion : smallFingers.left_mcp_flexion,
      mcpExtension: isRight ? smallFingers.right_mcp_extension : smallFingers.left_mcp_extension,
      radialSensLevel: isRight ? smallFingers.right_radial_two_point : smallFingers.left_radial_two_point,
      radialSensPercent: isRight ? smallFingers.right_radial_two_point_percent : smallFingers.left_radial_two_point_percent,
      ulnarSensLevel: isRight ? smallFingers.right_ulnar_two_point : smallFingers.left_ulnar_two_point,
      ulnarSensPercent: isRight ? smallFingers.right_ulnar_two_point_percent : smallFingers.left_ulnar_two_point_percent,
      amputationPercent: isRight ? (smallFingers.right_amputation_percent || 0) : (smallFingers.left_amputation_percent || 0),
      triggering: isRight ? (smallFingers.right_triggering || "") : (smallFingers.left_triggering || ""),
      extensorSubluxation: isRight ? (smallFingers.right_extensor_subluxation_mcp || "") : (smallFingers.left_extensor_subluxation_mcp || ""),
      digitWorth: 10,
    };

    // ===================================
    // HELPER & CALCULATION FUNCTIONS
    // ===================================

    // --- Motion Calculation Functions ---
    const calcThumbIPFlexion = HAND_IMPAIRMENT.thumbIpFlex;
    const calcThumbIPExtension = HAND_IMPAIRMENT.thumbIpExt;
    const calcThumbMCPFlexion = HAND_IMPAIRMENT.thumbMcpFlex;
    const calcThumbMCPExtension = HAND_IMPAIRMENT.thumbMcpExt;
    const calcThumbCMCRadialAbduction = HAND_IMPAIRMENT.cmcRadialAbduction;
    const calcThumbCMCAdduction = HAND_IMPAIRMENT.cmcAdduction;
    const calcThumbCMCOpposition = HAND_IMPAIRMENT.cmcOpposition;
    const calcDIPFlex = HAND_IMPAIRMENT.dipFlex;
    const calcDIPExt = HAND_IMPAIRMENT.dipExt;
    const calcPIPFlex = HAND_IMPAIRMENT.pipFlex;
    const calcPIPExt = HAND_IMPAIRMENT.pipExt;
    const calcMCPFlex = HAND_IMPAIRMENT.mcpFlex;
    const calcMCPExt = HAND_IMPAIRMENT.mcpExt;

    // --- Generic Helper Functions ---
    function twoPointToLossFactor(mm: number): number { if (mm > 15) return 1; if (mm > 6) return 0.5; return 0; }
    function calcTriggerSeverity(trigger: string): number {
      if (!trigger) return 0; const t = trigger.toLowerCase().trim(); if (t.includes("none")) return 0; if (t.includes("mild")) return 20; if (t.includes("moderate")) return 40; if (t.includes("severe")) return 60; if (t.startsWith("n")) return 0; if (t.startsWith("mi")) return 20; if (t.startsWith("mod")) return 40; if (t.startsWith("s")) return 60; return 0;
    }
    function convertDigitToHand(digitPercent: number, digitWorth: number): number { if (digitPercent === null || digitPercent === undefined) return 0; return Math.round(digitPercent * (digitWorth / 100)); }
    function getLossText(lossFactor: number): string { if (lossFactor === 1) return `total loss (1.0 [Table 16-5, p447])`; if (lossFactor === 0.5) return `partial loss (0.5 [Table 16-5, p447])`; return `no loss (0 [Table 16-5, p447])`; }

    // ===================================
    // IMPAIRMENT CALCULATIONS
    // ===================================

    // --- Thumb Calculations ---
    const thumbIPFlexResult = calcThumbIPFlexion(thumbData.ipFlexion);
    const thumbIPExtResult = calcThumbIPExtension(thumbData.ipExtension);
    const thumbMCPFlexResult = calcThumbMCPFlexion(thumbData.mcpFlexion);
    const thumbMCPExtResult = calcThumbMCPExtension(thumbData.mcpExtension);
    const thumbCMCRadAbdResult = calcThumbCMCRadialAbduction(thumbData.cmcRadialAbduction);
    const thumbCMCAddResult = calcThumbCMCAdduction(thumbData.cmcAdduction);
    const thumbCMCOppResult = calcThumbCMCOpposition(thumbData.cmcOpposition);
    const thumbMotion = thumbIPFlexResult + thumbIPExtResult + thumbMCPFlexResult + thumbMCPExtResult + thumbCMCRadAbdResult + thumbCMCAddResult + thumbCMCOppResult;
    const thumbRadialLoss = twoPointToLossFactor(thumbData.radialSensLevel);
    const thumbUlnarLoss = twoPointToLossFactor(thumbData.ulnarSensLevel);
    const thumbRadialSensory = Math.round(20 * (thumbData.radialSensPercent / 100) * thumbRadialLoss);
    const thumbUlnarSensory = Math.round(30 * (thumbData.ulnarSensPercent / 100) * thumbUlnarLoss);
    const thumbSensory = thumbRadialSensory + thumbUlnarSensory;
    const thumbTriggeringSeverity = calcTriggerSeverity(thumbData.triggering);
    const combinedThumbDigit = combineAMAValues([thumbMotion, thumbSensory, thumbData.amputationPercent, thumbTriggeringSeverity]);
    const thumbToHand = convertDigitToHand(combinedThumbDigit, thumbData.digitWorth);

    // --- Index Finger Calculations ---
    const dipFlex = calcDIPFlex(indexData.dipFlexion);
    const dipExt = calcDIPExt(indexData.dipExtension);
    const pipFlex = calcPIPFlex(indexData.pipFlexion);
    const pipExt = calcPIPExt(indexData.pipExtension);
    const mcpFlex = calcMCPFlex(indexData.mcpFlexion);
    const mcpExt = calcMCPExt(indexData.mcpExtension);
    const dipSum = dipFlex + dipExt;
    const pipSum = pipFlex + pipExt;
    const mcpSum = mcpFlex + mcpExt;
    const totalIndexMotion = combineAMAValues([dipSum, pipSum, mcpSum]);
    const indexRadLoss = twoPointToLossFactor(indexData.radialSensLevel || 0);
    const indexUlnLoss = twoPointToLossFactor(indexData.ulnarSensLevel || 0);
    const indexRadialSensory = Math.round(30 * ((indexData.radialSensPercent || 0) / 100) * indexRadLoss);
    const indexUlnarSensory = Math.round(20 * ((indexData.ulnarSensPercent || 0) / 100) * indexUlnLoss);
    const indexSensory = indexRadialSensory + indexUlnarSensory;
    const indexTriggeringSeverity = calcTriggerSeverity(indexData.triggering || "");
    const indexExtensorSubluxationSeverity = calcExtensorSubluxationSeverity(indexData.extensorSubluxation || "");
    const combinedIndexDigit = combineAMAValues([totalIndexMotion, indexSensory, indexData.amputationPercent || 0, indexTriggeringSeverity, indexExtensorSubluxationSeverity]);
    const indexFingerToHandImpairment = convertDigitToHand(combinedIndexDigit, indexData.digitWorth);

    // --- Middle Finger Calculations ---
    const middleDipFlex = calcDIPFlex(middleData.dipFlexion);
    const middleDipExt = calcDIPExt(middleData.dipExtension);
    const middlePipFlex = calcPIPFlex(middleData.pipFlexion);
    const middlePipExt = calcPIPExt(middleData.pipExtension);
    const middleMcpFlex = calcMCPFlex(middleData.mcpFlexion);
    const middleMcpExt = calcMCPExt(middleData.mcpExtension);
    const middleDipSum = middleDipFlex + middleDipExt;
    const middlePipSum = middlePipFlex + middlePipExt;
    const middleMcpSum = middleMcpFlex + middleMcpExt;
    const totalMiddleMotion = combineAMAValues([middleDipSum, middlePipSum, middleMcpSum]);
    const middleRadLoss = twoPointToLossFactor(middleData.radialSensLevel || 0);
    const middleUlnLoss = twoPointToLossFactor(middleData.ulnarSensLevel || 0);
    const middleRadialSensory = Math.round(30 * ((middleData.radialSensPercent || 0) / 100) * middleRadLoss);
    const middleUlnarSensory = Math.round(20 * ((middleData.ulnarSensPercent || 0) / 100) * middleUlnLoss);
    const middleSensory = middleRadialSensory + middleUlnarSensory;
    const middleTriggeringSeverity = calcTriggerSeverity(middleData.triggering || "");
    const middleExtensorSubluxationSeverity = calcExtensorSubluxationSeverity(middleData.extensorSubluxation || "");
    const combinedMiddleDigit = combineAMAValues([totalMiddleMotion, middleSensory, middleData.amputationPercent, middleTriggeringSeverity, middleExtensorSubluxationSeverity]);
    const middleFingerToHandImpairment = convertDigitToHand(combinedMiddleDigit, middleData.digitWorth);

    // --- Ring Finger Calculations ---
    const ringDipFlex = calcRingDIPFlexion(ringData.dipFlexion);
    const ringDipExt = calcRingDIPExtension(ringData.dipExtension);
    const ringPipFlex = calcRingPIPFlexion(ringData.pipFlexion);
    const ringPipExt = calcRingPIPExtension(ringData.pipExtension);
    const ringMcpFlex = calcRingMCPFlexion(ringData.mcpFlexion);
    const ringMcpExt = calcRingMCPExtension(ringData.mcpExtension);
    const ringDipSum = ringDipFlex + ringDipExt;
    const ringPipSum = ringPipFlex + ringPipExt;
    const ringMcpSum = ringMcpFlex + ringMcpExt;
    const totalRingMotion = combineAMAValues([ringDipSum, ringPipSum, ringMcpSum]);
    const ringRadLoss = twoPointToLossFactor(ringData.radialSensLevel || 0);
    const ringUlnLoss = twoPointToLossFactor(ringData.ulnarSensLevel || 0);
    const ringRadialSensory = Math.round(30 * ((ringData.radialSensPercent || 0) / 100) * ringRadLoss);
    const ringUlnarSensory = Math.round(20 * ((ringData.ulnarSensPercent || 0) / 100) * ringUlnLoss);
    const ringSensory = ringRadialSensory + ringUlnarSensory;
    const ringTriggeringSeverity = calcTriggerSeverity(ringData.triggering || "");
    const ringExtensorSubluxationSeverity = calcExtensorSubluxationSeverity(ringData.extensorSubluxation || "");
    const combinedRingDigit = combineAMAValues([totalRingMotion, ringSensory, ringData.amputationPercent, ringTriggeringSeverity, ringExtensorSubluxationSeverity]);
    const ringFingerToHandImpairment = convertDigitToHand(combinedRingDigit, ringData.digitWorth);

    // --- Little Finger Calculations ---
    const littleDipFlex = calcSmallDIPFlexion(smallData.dipFlexion);
    const littleDipExt = calcSmallDIPExtension(smallData.dipExtension);
    const littlePipFlex = calcSmallPIPFlexion(smallData.pipFlexion);
    const littlePipExt = calcSmallPIPExtension(smallData.pipExtension);
    const littleMcpFlex = calcSmallMCPFlexion(smallData.mcpFlexion);
    const littleMcpExt = calcSmallMCPExtension(smallData.mcpExtension);
    const littleDipSum = littleDipFlex + littleDipExt;
    const littlePipSum = littlePipFlex + littlePipExt;
    const littleMcpSum = littleMcpFlex + littleMcpExt;
    const totalLittleMotion = combineAMAValues([littleDipSum, littlePipSum, littleMcpSum]);
    const littleRadLoss = twoPointToLossFactor(smallData.radialSensLevel || 0);
    const littleUlnLoss = twoPointToLossFactor(smallData.ulnarSensLevel || 0);
    // Little finger has different sensory percentages: ulnar 30% max, radial 20% max (opposite of other fingers)
    const littleRadialSensory = Math.round(20 * ((smallData.radialSensPercent || 0) / 100) * littleRadLoss);
    const littleUlnarSensory = Math.round(30 * ((smallData.ulnarSensPercent || 0) / 100) * littleUlnLoss);
    const littleSensory = littleRadialSensory + littleUlnarSensory;
    const littleTriggeringSeverity = calcTriggerSeverity(smallData.triggering || "");
    // Table 16-30 applies to every finger — the little finger was excluded here,
    // so a subluxation the Hands tab collected never reached the report.
    const littleExtensorSubluxationSeverity = calcExtensorSubluxationSeverity(smallData.extensorSubluxation || "");
    const combinedLittleDigit = combineAMAValues([totalLittleMotion, littleSensory, smallData.amputationPercent, littleTriggeringSeverity, littleExtensorSubluxationSeverity]);
    const littleFingerToHandImpairment = convertDigitToHand(combinedLittleDigit, smallData.digitWorth);

    // Calculate total hand impairment
    const totalHandImpairment = thumbToHand + indexFingerToHandImpairment + middleFingerToHandImpairment + ringFingerToHandImpairment + littleFingerToHandImpairment;
    let upperExtremityImpairment = 0;
    let wholePersonImpairment = 0;

    if (totalHandImpairment === 0) {
      return `<b><u>${capitalizedSide.toUpperCase()}:</u></b> No impairment.`;
    } else {
      upperExtremityImpairment = Math.round(totalHandImpairment * 0.9);
      wholePersonImpairment = Math.round(upperExtremityImpairment * 0.6);
      
      const introParagraph = `<b><u>${capitalizedSide.toUpperCase()}:</u></b>\n\nUsing Figure 16-1a on page 436, hand impairment is composed of four components: 1) abnormal motion, 2) amputation, 3) sensory loss, and 4) other disorders (triggering and extensor tendon subluxation). When range of motion measurements fall between standardized values, the corresponding impairment percentage is interpolated proportionally within the defined interval.`;
      
      let thumbReport = '';
      if (combinedThumbDigit === 0) {
        thumbReport = `Thumb (${capitalizedSide})\nno impairment.`;
      } else {
        const thumbMotionProcessed = thumbMotion > 0 ? `Motion:\n- IP flexion ${thumbData.ipFlexion}º = ${thumbIPFlexResult}%, extension ${thumbData.ipExtension}º = ${thumbIPExtResult}% [Fig 16-10 & 16-12, pp455–456]\n- MCP flexion ${thumbData.mcpFlexion}º = ${thumbMCPFlexResult}%, extension ${thumbData.mcpExtension}º = ${thumbMCPExtResult}% [Fig 16-13 & 16-15, pp456–457]\n- CMC radial abduction ${thumbData.cmcRadialAbduction}º = ${thumbCMCRadAbdResult}% [Figure 16-16 & Table 16-8a, p458-459]\n- CMC adduction ${thumbData.cmcAdduction} cm = ${thumbCMCAddResult}% [Figure 16-17 & Table 16-8b, p458-459]\n- Opposition ${thumbData.cmcOpposition} cm = ${thumbCMCOppResult}% [Figure 16-19 & Table 16-9, p460]\n→ Total motion = ${thumbIPFlexResult}% + ${thumbIPExtResult}% + ${thumbMCPFlexResult}% + ${thumbMCPExtResult}% + ${thumbCMCRadAbdResult}% + ${thumbCMCAddResult}% + ${thumbCMCOppResult}% = ${thumbMotion}% digit impairment [p460]` : '';
        const thumbSensoryProcessed = thumbSensory > 0 ? `Sensory:\n- Radial (20% max [Table 16-6, p448]): ${thumbData.radialSensLevel} mm → ${getLossText(thumbRadialLoss)}; ${thumbData.radialSensPercent}% digit length [Fig 16-6, p447] → 20% × ${thumbRadialLoss} × ${thumbData.radialSensPercent / 100} = ${thumbRadialSensory}%\n- Ulnar (30% max [Table 16-6, p448]): ${thumbData.ulnarSensLevel} mm → ${getLossText(thumbUlnarLoss)}; ${thumbData.ulnarSensPercent}% digit length [Fig 16-6, p447] → 30% × ${thumbUlnarLoss} × ${thumbData.ulnarSensPercent / 100} = ${thumbUlnarSensory}% \n→ Total sensory = ${thumbRadialSensory}% + ${thumbUlnarSensory}% = ${thumbSensory}% digit impairment` : '';
        const thumbAmputationProcessed = thumbData.amputationPercent > 0 ? `Amputation:\n- ${thumbData.amputationPercent}% of the digit = ${thumbData.amputationPercent}% digit impairment [Fig 16-4, p443]` : '';
        const thumbTriggerProcessed = thumbTriggeringSeverity > 0 ? `Triggering:\n- ${thumbData.triggering} = ${thumbTriggeringSeverity}% digit impairment [Table 16-29, p507]` : '';
        
        let parts = [`Thumb (${capitalizedSide})`];
        let impairmentValues = [`${thumbMotion}% (motion)`, `${thumbSensory}% (sensory)`, `${thumbTriggeringSeverity}% (triggering)`, `${thumbData.amputationPercent}% (amputation)`];
        
        if (thumbMotionProcessed) parts.push(thumbMotionProcessed);
        if (thumbSensoryProcessed) parts.push(thumbSensoryProcessed);
        if (thumbTriggerProcessed) parts.push(thumbTriggerProcessed);
        if (thumbAmputationProcessed) parts.push(thumbAmputationProcessed);

        parts.push(`→ Combine ${impairmentValues.join(', ')} via CVC [p604] = ${combinedThumbDigit}% digit impairment`);
        parts.push(`→ Digit worth = ${thumbData.digitWorth}% [Table 16-1, p438] → ${thumbData.digitWorth}% × ${combinedThumbDigit / 100} = ${thumbToHand}% hand impairment`);
        
        thumbReport = parts.join('\n\n');
      }
      
      // Generate detailed middle finger report
      let middleReport = '';
      if (combinedMiddleDigit === 0) {
        middleReport = `Middle Finger (${capitalizedSide})\nno impairment.`;
      } else {
        const middleMotionProcessed = totalMiddleMotion > 0 ? `Motion:\n- DIP flexion ${middleData.dipFlexion}º = ${middleDipFlex}%, extension ${middleData.dipExtension}º = ${middleDipExt}%; added = ${middleDipSum}% [Fig 16-20 & 16-21, p461]\n- PIP flexion ${middleData.pipFlexion}º = ${middlePipFlex}%, extension ${middleData.pipExtension}º = ${middlePipExt}%; added = ${middlePipSum}% [Fig 16-22 & 16-23, p463]\n- MCP flexion ${middleData.mcpFlexion}º = ${middleMcpFlex}%, extension ${middleData.mcpExtension}º = ${middleMcpExt}%; added = ${middleMcpSum}% [Fig 16-24 & 16-25, p464]\n→ Combine ${middleDipSum}% (DIP), ${middlePipSum}% (PIP), ${middleMcpSum}% (MCP) via CVC = ${totalMiddleMotion}% digit impairment [p604]` : '';
        const middleSensoryProcessed = middleSensory > 0 ? `Sensory:\n- Radial (30% max [Table 16-7, p448]): ${middleData.radialSensLevel} mm → ${getLossText(middleRadLoss)}; ${middleData.radialSensPercent}% digit length [Fig 16-6, p447] → 30% × ${middleRadLoss} × ${middleData.radialSensPercent / 100} = ${middleRadialSensory}%\n- Ulnar (20% max [Table 16-7, p448]): ${middleData.ulnarSensLevel} mm → ${getLossText(middleUlnLoss)}; ${middleData.ulnarSensPercent}% digit length [Fig 16-6, p447] → 20% × ${middleUlnLoss} × ${middleData.ulnarSensPercent / 100} = ${middleUlnarSensory}%\n→ Total sensory = ${middleRadialSensory}% + ${middleUlnarSensory}% = ${middleSensory}% digit impairment` : '';
        const middleAmputationProcessed = middleData.amputationPercent > 0 ? `Amputation:\n- ${middleData.amputationPercent}% of the digit = ${middleData.amputationPercent}% digit impairment [Fig 16-4, p443]` : '';
        const middleTriggerProcessed = middleTriggeringSeverity > 0 ? `Triggering:\n- ${middleData.triggering} = ${middleTriggeringSeverity}% digit impairment [Table 16-29, p507]` : '';
        const middleSubluxationProcessed = middleExtensorSubluxationSeverity > 0 ? `Extensor tendon subluxation:\n- ${middleData.extensorSubluxation} = ${middleExtensorSubluxationSeverity}% digit impairment [Table 16-30, p507]` : '';
        
        let parts = [`Middle Finger (${capitalizedSide})`];
        let impairmentValues = [`${totalMiddleMotion}% (motion)`, `${middleSensory}% (sensory)`, `${middleTriggeringSeverity}% (triggering)`, `${middleExtensorSubluxationSeverity}% (subluxation)`, `${middleData.amputationPercent}% (amputation)`];
        
        if (middleMotionProcessed) parts.push(middleMotionProcessed);
        if (middleSensoryProcessed) parts.push(middleSensoryProcessed);
        if (middleTriggerProcessed) parts.push(middleTriggerProcessed);
        if (middleSubluxationProcessed) parts.push(middleSubluxationProcessed);
        if (middleAmputationProcessed) parts.push(middleAmputationProcessed);

        parts.push(`→ Combine ${impairmentValues.join(', ')} via CVC [p604] = ${combinedMiddleDigit}% digit impairment`);
        parts.push(`→ Digit worth = ${middleData.digitWorth}% [Table 16-1, p438] → ${middleData.digitWorth}% × ${combinedMiddleDigit / 100} = ${middleFingerToHandImpairment}% hand impairment`);
        
        middleReport = parts.join('\n\n');
      }

      // Generate detailed index finger report  
      let indexReport = '';
      if (combinedIndexDigit === 0) {
        indexReport = `Index Finger (${capitalizedSide})\nno impairment.`;
      } else {
        const indexMotionProcessed = totalIndexMotion > 0 ? `Motion:\n- DIP flexion ${indexData.dipFlexion}º = ${dipFlex}%, extension ${indexData.dipExtension}º = ${dipExt}%; added = ${dipSum}% [Fig 16-20 & 16-21, p461]\n- PIP flexion ${indexData.pipFlexion}º = ${pipFlex}%, extension ${indexData.pipExtension}º = ${pipExt}%; added = ${pipSum}% [Fig 16-22 & 16-23, p463]\n- MCP flexion ${indexData.mcpFlexion}º = ${mcpFlex}%, extension ${indexData.mcpExtension}º = ${mcpExt}%; added = ${mcpSum}% [Fig 16-24 & 16-25, p464]\n→ Combine ${dipSum}% (DIP), ${pipSum}% (PIP), ${mcpSum}% (MCP) via CVC = ${totalIndexMotion}% digit impairment [p604]` : '';
        const indexSensoryProcessed = indexSensory > 0 ? `Sensory:\n- Radial (30% max [Table 16-7, p448]): ${indexData.radialSensLevel} mm → ${getLossText(indexRadLoss)}; ${indexData.radialSensPercent}% digit length [Fig 16-6, p447] → 30% × ${indexRadLoss} × ${indexData.radialSensPercent / 100} = ${indexRadialSensory}%\n- Ulnar (20% max [Table 16-7, p448]): ${indexData.ulnarSensLevel} mm → ${getLossText(indexUlnLoss)}; ${indexData.ulnarSensPercent}% digit length [Fig 16-6, p447] → 20% × ${indexUlnLoss} × ${indexData.ulnarSensPercent / 100} = ${indexUlnarSensory}%\n→ Total sensory = ${indexRadialSensory}% + ${indexUlnarSensory}% = ${indexSensory}% digit impairment` : '';
        const indexAmputationProcessed = indexData.amputationPercent > 0 ? `Amputation:\n- ${indexData.amputationPercent}% of the digit = ${indexData.amputationPercent}% digit impairment [Fig 16-4, p443]` : '';
        const indexTriggerProcessed = indexTriggeringSeverity > 0 ? `Triggering:\n- ${indexData.triggering} = ${indexTriggeringSeverity}% digit impairment [Table 16-29, p507]` : '';
        const indexSubluxationProcessed = indexExtensorSubluxationSeverity > 0 ? `Extensor tendon subluxation:\n- ${indexData.extensorSubluxation} = ${indexExtensorSubluxationSeverity}% digit impairment [Table 16-30, p507]` : '';
        
        let parts = [`Index Finger (${capitalizedSide})`];
        let impairmentValues = [`${totalIndexMotion}% (motion)`, `${indexSensory}% (sensory)`, `${indexTriggeringSeverity}% (triggering)`, `${indexExtensorSubluxationSeverity}% (extensor subluxation)`, `${indexData.amputationPercent}% (amputation)`];
        
        if (indexMotionProcessed) parts.push(indexMotionProcessed);
        if (indexSensoryProcessed) parts.push(indexSensoryProcessed);
        if (indexTriggerProcessed) parts.push(indexTriggerProcessed);
        if (indexSubluxationProcessed) parts.push(indexSubluxationProcessed);
        if (indexAmputationProcessed) parts.push(indexAmputationProcessed);

        parts.push(`→ Combine ${impairmentValues.join(', ')} via CVC [p604] = ${combinedIndexDigit}% digit impairment`);
        parts.push(`→ Digit worth = ${indexData.digitWorth}% [Table 16-1, p438] → ${indexData.digitWorth}% × ${combinedIndexDigit / 100} = ${indexFingerToHandImpairment}% hand impairment`);
        
        indexReport = parts.join('\n\n');
      }

      // Generate detailed ring finger report  
      let ringReport = '';
      if (combinedRingDigit === 0) {
        ringReport = `Ring Finger (${capitalizedSide})\nno impairment.`;
      } else {
        const ringMotionProcessed = totalRingMotion > 0 ? `Motion:\n- DIP flexion ${ringData.dipFlexion}º = ${ringDipFlex}%, extension ${ringData.dipExtension}º = ${ringDipExt}%; added = ${ringDipSum}% [Fig 16-20 & 16-21, p461]\n- PIP flexion ${ringData.pipFlexion}º = ${ringPipFlex}%, extension ${ringData.pipExtension}º = ${ringPipExt}%; added = ${ringPipSum}% [Fig 16-22 & 16-23, p463]\n- MCP flexion ${ringData.mcpFlexion}º = ${ringMcpFlex}%, extension ${ringData.mcpExtension}º = ${ringMcpExt}%; added = ${ringMcpSum}% [Fig 16-24 & 16-25, p464]\n→ Combine ${ringDipSum}% (DIP), ${ringPipSum}% (PIP), ${ringMcpSum}% (MCP) via CVC = ${totalRingMotion}% digit impairment [p604]` : '';
        const ringSensoryProcessed = ringSensory > 0 ? `Sensory:\n- Radial (30% max [Table 16-7, p448]): ${ringData.radialSensLevel} mm → ${getLossText(ringRadLoss)}; ${ringData.radialSensPercent}% digit length [Fig 16-6, p447] → 30% × ${ringRadLoss} × ${ringData.radialSensPercent / 100} = ${ringRadialSensory}%\n- Ulnar (20% max [Table 16-7, p448]): ${ringData.ulnarSensLevel} mm → ${getLossText(ringUlnLoss)}; ${ringData.ulnarSensPercent}% digit length [Fig 16-6, p447] → 20% × ${ringUlnLoss} × ${ringData.ulnarSensPercent / 100} = ${ringUlnarSensory}%\n→ Total sensory = ${ringRadialSensory}% + ${ringUlnarSensory}% = ${ringSensory}% digit impairment` : '';
        const ringAmputationProcessed = ringData.amputationPercent > 0 ? `Amputation:\n- ${ringData.amputationPercent}% of the digit = ${ringData.amputationPercent}% digit impairment [Fig 16-4, p443]` : '';
        const ringTriggerProcessed = ringTriggeringSeverity > 0 ? `Triggering:\n- ${ringData.triggering} = ${ringTriggeringSeverity}% digit impairment [Table 16-29, p507]` : '';
        const ringSubluxationProcessed = ringExtensorSubluxationSeverity > 0 ? `Extensor tendon subluxation:\n- ${ringData.extensorSubluxation} = ${ringExtensorSubluxationSeverity}% digit impairment [Table 16-30, p507]` : '';
        
        let parts = [`Ring Finger (${capitalizedSide})`];
        let impairmentValues = [`${totalRingMotion}% (motion)`, `${ringSensory}% (sensory)`, `${ringTriggeringSeverity}% (triggering)`, `${ringExtensorSubluxationSeverity}% (subluxation)`, `${ringData.amputationPercent}% (amputation)`];
        
        if (ringMotionProcessed) parts.push(ringMotionProcessed);
        if (ringSensoryProcessed) parts.push(ringSensoryProcessed);
        if (ringTriggerProcessed) parts.push(ringTriggerProcessed);
        if (ringSubluxationProcessed) parts.push(ringSubluxationProcessed);
        if (ringAmputationProcessed) parts.push(ringAmputationProcessed);

        parts.push(`→ Combine ${impairmentValues.join(', ')} via CVC [p604] = ${combinedRingDigit}% digit impairment`);
        parts.push(`→ Digit worth = ${ringData.digitWorth}% [Table 16-1, p438] → ${ringData.digitWorth}% × ${combinedRingDigit / 100} = ${ringFingerToHandImpairment}% hand impairment`);
        
        ringReport = parts.join('\n\n');
      }

      // Generate detailed little finger report  
      let littleReport = '';
      if (combinedLittleDigit === 0) {
        littleReport = `Little Finger (${capitalizedSide})\nno impairment.`;
      } else {
        const littleMotionProcessed = totalLittleMotion > 0 ? `Motion:\n- DIP flexion ${smallData.dipFlexion}º = ${littleDipFlex}%, extension ${smallData.dipExtension}º = ${littleDipExt}%; added = ${littleDipSum}% [Fig 16-20 & 16-21, p461]\n- PIP flexion ${smallData.pipFlexion}º = ${littlePipFlex}%, extension ${smallData.pipExtension}º = ${littlePipExt}%; added = ${littlePipSum}% [Fig 16-22 & 16-23, p463]\n- MCP flexion ${smallData.mcpFlexion}º = ${littleMcpFlex}%, extension ${smallData.mcpExtension}º = ${littleMcpExt}%; added = ${littleMcpSum}% [Fig 16-24 & 16-25, p464]\n→ Combine ${littleDipSum}% (DIP), ${littlePipSum}% (PIP), ${littleMcpSum}% (MCP) via CVC = ${totalLittleMotion}% digit impairment [p604]` : '';
        const littleSensoryProcessed = littleSensory > 0 ? `Sensory:\n- Radial (20% max [Table 16-7, p448]): ${smallData.radialSensLevel} mm → ${getLossText(littleRadLoss)}; ${smallData.radialSensPercent}% digit length [Fig 16-6, p447] → 20% × ${littleRadLoss} × ${smallData.radialSensPercent / 100} = ${littleRadialSensory}%\n- Ulnar (30% max [Table 16-7, p448]): ${smallData.ulnarSensLevel} mm → ${getLossText(littleUlnLoss)}; ${smallData.ulnarSensPercent}% digit length [Fig 16-6, p447] → 30% × ${littleUlnLoss} × ${smallData.ulnarSensPercent / 100} = ${littleUlnarSensory}%\n→ Total sensory = ${littleRadialSensory}% + ${littleUlnarSensory}% = ${littleSensory}% digit impairment` : '';
        const littleAmputationProcessed = smallData.amputationPercent > 0 ? `Amputation:\n- ${smallData.amputationPercent}% of the digit = ${smallData.amputationPercent}% digit impairment [Fig 16-4, p443]` : '';
        const littleTriggerProcessed = littleTriggeringSeverity > 0 ? `Triggering:\n- ${smallData.triggering} = ${littleTriggeringSeverity}% digit impairment [Table 16-29, p507]` : '';
        const littleSubluxationProcessed = littleExtensorSubluxationSeverity > 0 ? `Extensor tendon subluxation:\n- ${smallData.extensorSubluxation} = ${littleExtensorSubluxationSeverity}% digit impairment [Table 16-30, p507]` : '';
        
        let parts = [`Little Finger (${capitalizedSide})`];
        let impairmentValues = [`${totalLittleMotion}% (motion)`, `${littleSensory}% (sensory)`, `${littleTriggeringSeverity}% (triggering)`, `${littleExtensorSubluxationSeverity}% (extensor subluxation)`, `${smallData.amputationPercent}% (amputation)`];
        
        if (littleMotionProcessed) parts.push(littleMotionProcessed);
        if (littleSensoryProcessed) parts.push(littleSensoryProcessed);
        if (littleTriggerProcessed) parts.push(littleTriggerProcessed);
        if (littleSubluxationProcessed) parts.push(littleSubluxationProcessed);
        if (littleAmputationProcessed) parts.push(littleAmputationProcessed);

        parts.push(`→ Combine ${impairmentValues.join(', ')} via CVC [p604] = ${combinedLittleDigit}% digit impairment`);
        parts.push(`→ Digit worth = ${smallData.digitWorth}% [Table 16-1, p438] → ${smallData.digitWorth}% × ${combinedLittleDigit / 100} = ${littleFingerToHandImpairment}% hand impairment`);
        
        littleReport = parts.join('\n\n');
      }

      const finalDetailedReport = [introParagraph, '–––––', thumbReport, '–––––', indexReport, '–––––', middleReport, '–––––', ringReport, '–––––', littleReport, '–––––', `Totals`, `Hand impairment (by addition of digits): ${thumbToHand}% (thumb) + ${indexFingerToHandImpairment}% (index) + ${middleFingerToHandImpairment}% (middle) + ${ringFingerToHandImpairment}% (ring) + ${littleFingerToHandImpairment}% (little) = ${totalHandImpairment}% hand impairment`, `Upper extremity impairment: ${totalHandImpairment}% × 0.9 = ${upperExtremityImpairment}% UEI [Table 16-2, p439]`, `Whole person impairment: ${upperExtremityImpairment}% × 0.6 = ${wholePersonImpairment}% WPI [Table 16-3, p439]\n\nThis level of impairment is felt to most accurately reflect the residual effects of the work injury and takes into account the Almaraz/Guzman II decision.`].join('\n\n');
      
      return finalDetailedReport;
    }
  }, []);
  
  // Shoulder calculation functions - exact n8n logic






  // Combine values function for shoulder acromioplasty - exact n8n logic
  const combineValues = combine; // alias to shared AMA combine (utils/amaMath)

  // Generate shoulder narrative with exact n8n logic
  const generateShoulderNarrative = (shoulderData: any, side: 'right' | 'left'): string | null => {
    if (!shoulderData) return null;
    
    const prefix = side === 'right' ? 'right_' : 'left_';
    const flexion = shoulderData[`${prefix}flexion`];
    const extension = shoulderData[`${prefix}extension`];
    const abduction = shoulderData[`${prefix}abduction`];
    const adduction = shoulderData[`${prefix}adduction`];
    const internalRotation = shoulderData[`${prefix}internal_rotation`];
    const externalRotation = shoulderData[`${prefix}external_rotation`];
    
    // Check for acromioplasty flag
    const includeAcromioplasty = examData.shoulders?.[`${prefix}includeAcromioplasty`] === true;
    
    if (flexion === null && extension === null && abduction === null && 
        adduction === null && internalRotation === null && externalRotation === null) {
      return null;
    }
    
    const flexionImpairment = calculateFlexion(flexion);
    const extensionImpairment = calculateExtension(extension);
    const abductionImpairment = calculateAbduction(abduction);
    const adductionImpairment = calculateAdduction(adduction);
    const irImpairment = calculateInternalRotation(internalRotation);
    const erImpairment = calculateExternalRotation(externalRotation);
    
    const motionImpairmentSum = flexionImpairment + extensionImpairment + abductionImpairment + adductionImpairment + irImpairment + erImpairment;
    const acromioplastyValue = 10;
    
    const finalUeImpairment = includeAcromioplasty
      ? combineValues(motionImpairmentSum, acromioplastyValue)
      : motionImpairmentSum;
    
    const wholePersonImpairment = Math.round(finalUeImpairment * 0.6);
    
    // Build the report conditionally - exact n8n structure
    const capitalizedSide = side.charAt(0).toUpperCase() + side.slice(1) + ' shoulder';
    
    if (finalUeImpairment === 0) {
      return `<b><u>${capitalizedSide.toUpperCase()}:</u></b> No impairment based on range of motion.`;
    }
    
    const intro = `<b><u>${capitalizedSide.toUpperCase()}:</u></b>\n\nImpairment rating is based on range of motion deficit. When measurements fall between standardized values, the corresponding impairment percentage is interpolated proportionally.`;
    
    const motionBreakdown = `Motion:\n` +
      `- Flexion ${flexion}° = ${flexionImpairment}% Upper Extremity Impairment [Figure 16-40, p476]\n` +
      `- Extension ${extension}° = ${extensionImpairment}% Upper Extremity Impairment [Figure 16-40, p476]\n` +
      `- Abduction ${abduction}° = ${abductionImpairment}% Upper Extremity Impairment [Figure 16-43, p477]\n` +
      `- Adduction ${adduction}° = ${adductionImpairment}% Upper Extremity Impairment [Figure 16-43, p477]\n` +
      `- Internal Rotation ${internalRotation}° = ${irImpairment}% Upper Extremity Impairment [Figure 16-46, p479]\n` +
      `- External Rotation ${externalRotation}° = ${erImpairment}% Upper Extremity Impairment [Figure 16-46, p479]`;

    const motionTotal = `→ Total motion impairment = ${flexionImpairment}% + ${extensionImpairment}% + ${abductionImpairment}% + ${adductionImpairment}% + ${irImpairment}% + ${erImpairment}% = ${motionImpairmentSum}% Upper Extremity Impairment.`;
    
    let acromioplastyReport = '';
    if (includeAcromioplasty) {
      acromioplastyReport = `Acromioplasty:\n` +
        `- The acromioplasty is rated as a distal clavicle resection, adding 10% UEI [Table 16-27, p506].\n` +
        `→ Combine ${motionImpairmentSum}% (motion) with 10% (acromioplasty) = ${finalUeImpairment}% Upper Extremity Impairment.`;
    }
    
    const totals = `Totals:\n` +
      `- Final Upper Extremity Impairment = ${finalUeImpairment}%\n` +
      `→ Whole Person Impairment = ${finalUeImpairment}% × 0.6 = ${wholePersonImpairment}% WPI [Table 16-3, p439].\n\nThis level of impairment is felt to most accurately reflect the residual effects of the work injury and takes into account the Almaraz/Guzman II decision.`;

    // Assemble the final narrative from the parts
    const narrative = [intro, motionBreakdown, motionTotal, acromioplastyReport, totals]
      .filter(part => part) // Removes any empty strings (like acromioplastyReport)
      .join('\n\n');
    
    return narrative;
  };

  // Wrist calculation functions - exact n8n logic




  // Generate wrist narrative with exact n8n logic
  const generateWristNarrative = (wristData: any, side: 'right' | 'left'): string | null => {
    if (!wristData) return null;
    
    const prefix = side === 'right' ? 'right_' : 'left_';
    const flexion = wristData[`${prefix}flexion`];
    const extension = wristData[`${prefix}extension`];
    const radialDeviation = wristData[`${prefix}radial_deviation`];
    const ulnarDeviation = wristData[`${prefix}ulnar_deviation`];
    
    if (flexion === null && extension === null && radialDeviation === null && ulnarDeviation === null) {
      return null;
    }
    
    const flexionImpairment = calculateWristFlexion(flexion);
    const extensionImpairment = calculateWristExtension(extension);
    const radialImpairment = calculateWristRadialDeviation(radialDeviation);
    const ulnarImpairment = calculateWristUlnarDeviation(ulnarDeviation);
    
    const motionImpairmentSum = flexionImpairment + extensionImpairment + radialImpairment + ulnarImpairment;
    const wholePersonImpairment = Math.round(motionImpairmentSum * 0.6);
    
    const capitalizedSide = side.charAt(0).toUpperCase() + side.slice(1) + ' wrist';
    
    if (motionImpairmentSum === 0) {
      return `<b><u>${capitalizedSide.toUpperCase()}:</u></b> No impairment based on range of motion.`;
    }
    
    const intro = `<b><u>${capitalizedSide.toUpperCase()}:</u></b>\n\nImpairment rating is based on range of motion deficit. When measurements fall between standardized values, the corresponding impairment percentage is interpolated proportionally.`;
    
    const motionBreakdown = `Motion:\n` +
      `- Flexion ${flexion}° = ${flexionImpairment}% Upper Extremity Impairment [Figure 16-28, p467]\n` +
      `- Extension ${extension}° = ${extensionImpairment}% Upper Extremity Impairment [Figure 16-28, p467]\n` +
      `- Radial Deviation ${radialDeviation}° = ${radialImpairment}% Upper Extremity Impairment [Figure 16-31, p469]\n` +
      `- Ulnar Deviation ${ulnarDeviation}° = ${ulnarImpairment}% Upper Extremity Impairment [Figure 16-31, p469]`;

    const motionTotal = `→ Total motion impairment = ${flexionImpairment}% + ${extensionImpairment}% + ${radialImpairment}% + ${ulnarImpairment}% = ${motionImpairmentSum}% Upper Extremity Impairment.`;
    
    const totals = `Totals:\n` +
      `- Final Upper Extremity Impairment = ${motionImpairmentSum}%\n` +
      `→ Whole Person Impairment = ${motionImpairmentSum}% × 0.6 = ${wholePersonImpairment}% WPI [Table 16-3, p439].\n\nThis level of impairment is felt to most accurately reflect the residual effects of the work injury and takes into account the Almaraz/Guzman II decision.`;

    return [intro, motionBreakdown, motionTotal, totals].join('\n\n');
  };

  // Hip calculation functions - exact n8n logic per Table 17-9






  // Generate hip narrative with exact n8n logic
  const generateHipNarrative = (hipData: any, side: 'right' | 'left'): string | null => {
    if (!hipData) return null;
    
    const prefix = side === 'right' ? 'right_' : 'left_';
    const flexion = hipData[`${prefix}flexion`];
    // `?? null`: the legacy `<side>_extension` field no longer exists, so `||` fell
    // through to undefined, the "side has no data" check (=== null) missed it, and an
    // unexamined hip printed "No impairment" and a 0% row.
    const extensionContracture = hipData[`${prefix}extension_contracture`] ?? hipData[`${prefix}extension`] ?? null;
    const internalRotation = hipData[`${prefix}internal_rotation`];
    const externalRotation = hipData[`${prefix}external_rotation`];
    const adduction = hipData[`${prefix}adduction`];
    const abductionContracture = hipData[`${prefix}abduction_contracture`];
    
    // Check if there's any data for this side
    if (flexion === null && extensionContracture === null && internalRotation === null && 
        externalRotation === null && adduction === null && abductionContracture === null) {
      return null;
    }
    
    const flexionImpairment = calculateHipFlexion(flexion);
    const extensionImpairment = calculateHipExtensionContracture(extensionContracture);
    const internalRotationImpairment = calculateHipInternalRotation(internalRotation);
    const externalRotationImpairment = calculateHipExternalRotation(externalRotation);
    const adductionImpairment = calculateHipAdduction(adduction);
    const abductionContractureImpairment = calculateHipAbductionContracture(abductionContracture);
    
    const motionImpairmentSum = flexionImpairment + extensionImpairment + internalRotationImpairment + externalRotationImpairment + adductionImpairment + abductionContractureImpairment;
    const wholePersonImpairment = Math.round(motionImpairmentSum * 0.4);
    
    const capitalizedSide = side.charAt(0).toUpperCase() + side.slice(1) + ' hip';
    
    if (motionImpairmentSum === 0) {
      return `<b><u>${capitalizedSide.toUpperCase()}:</u></b> No impairment based on range of motion.`;
    }
    
    const fmtDeg = (v: number | null) => (v == null) ? 'None' : `${v}°`;
    
    const intro = `<b><u>${capitalizedSide.toUpperCase()}:</u></b>\n\nImpairment rating is based on range of motion deficit.`;
    
    const motionBreakdown = `Motion:\n` +
      `- Flexion ${fmtDeg(flexion)} = ${flexionImpairment}% Lower Extremity Impairment [Table 17-9, p537]\n` +
      `- Extension Contracture ${fmtDeg(extensionContracture)} = ${extensionImpairment}% Lower Extremity Impairment [Table 17-9, p537]\n` +
      `- Internal Rotation ${fmtDeg(internalRotation)} = ${internalRotationImpairment}% Lower Extremity Impairment [Table 17-9, p537]\n` +
      `- External Rotation ${fmtDeg(externalRotation)} = ${externalRotationImpairment}% Lower Extremity Impairment [Table 17-9, p537]\n` +
      `- Adduction ${fmtDeg(adduction)} = ${adductionImpairment}% Lower Extremity Impairment [Table 17-9, p537]\n` +
      `- Abduction Contracture ${fmtDeg(abductionContracture)} = ${abductionContractureImpairment}% Lower Extremity Impairment [Table 17-9, p537]`;

    const motionTotal = `→ Total impairment = ${flexionImpairment}% + ${extensionImpairment}% + ${internalRotationImpairment}% + ${externalRotationImpairment}% + ${adductionImpairment}% + ${abductionContractureImpairment}% = ${motionImpairmentSum}% Lower Extremity Impairment.`;
    
    const totals = `Totals:\n` +
      `- Final Lower Extremity Impairment = ${motionImpairmentSum}%\n` +
      `→ Whole Person Impairment = ${motionImpairmentSum}% × 0.4 = ${wholePersonImpairment}% WPI [Table 17-3, p527].\n\nThis level of impairment is felt to most accurately reflect the residual effects of the work injury and takes into account the Almaraz/Guzman II decision.`;

    return [intro, motionBreakdown, motionTotal, totals].join('\n\n');
  };

  // Knee calculation functions - exact n8n logic per Table 17-10




  // Generate knee narrative with exact n8n logic
  const generateKneeNarrative = (kneeData: any, side: 'right' | 'left'): string | null => {
    if (!kneeData) return null;
    
    const prefix = side === 'right' ? 'right_' : 'left_';
    const flexion = kneeData[`${prefix}flexion`];
    const flexionContracture = kneeData[`${prefix}flexion_contracture_passive`];
    const varus = kneeData[`${prefix}varus`];
    const valgus = kneeData[`${prefix}valgus`];
    
    // Check if there's any data for this side
    if (flexion === null && flexionContracture === null && varus === null && valgus === null) {
      return null;
    }
    
    const flexionImpairment = calculateKneeFlexion(flexion);
    const flexionContractureImpairment = calculateKneeFlexionContracture(flexionContracture);
    const varusImpairment = calculateKneeVarus(varus);
    const valgusImpairment = calculateKneeValgus(valgus);
    
    const motionImpairmentSum = flexionImpairment + flexionContractureImpairment + varusImpairment + valgusImpairment;
    const wholePersonImpairment = Math.round(motionImpairmentSum * 0.4);
    
    const capitalizedSide = side.charAt(0).toUpperCase() + side.slice(1) + ' knee';
    
    if (motionImpairmentSum === 0) {
      return `<b><u>${capitalizedSide.toUpperCase()}:</u></b> No impairment based on range of motion.`;
    }
    
    const intro = `<b><u>${capitalizedSide.toUpperCase()}:</u></b>\n\nImpairment rating is based on range of motion deficit.`;
    
    const motionBreakdown = `Motion:\n` +
      `- Flexion ${flexion}° = ${flexionImpairment}% Lower Extremity Impairment [Table 17-10, p537]\n` +
      `- Flexion Contracture ${flexionContracture}° = ${flexionContractureImpairment}% Lower Extremity Impairment [Table 17-10, p537]\n` +
      `- Varus Deformity ${varus}° = ${varusImpairment}% Lower Extremity Impairment [Table 17-10, p537]\n` +
      `- Valgus Deformity ${valgus}° = ${valgusImpairment}% Lower Extremity Impairment [Table 17-10, p537]`;

    const motionTotal = `→ Total impairment = ${flexionImpairment}% + ${flexionContractureImpairment}% + ${varusImpairment}% + ${valgusImpairment}% = ${motionImpairmentSum}% Lower Extremity Impairment.`;
    
    const totals = `Totals:\n` +
      `- Final Lower Extremity Impairment = ${motionImpairmentSum}%\n` +
      `→ Whole Person Impairment = ${motionImpairmentSum}% × 0.4 = ${wholePersonImpairment}% WPI [Table 17-3, p527].\n\nThis level of impairment is felt to most accurately reflect the residual effects of the work injury and takes into account the Almaraz/Guzman II decision.`;

    return [intro, motionBreakdown, motionTotal, totals].join('\n\n');
  };

  // Ankle calculation functions - exact n8n logic per Table 17-11 and 17-12




  // Generate ankle narrative with exact n8n logic
  const generateAnkleNarrative = (ankleData: any, side: 'right' | 'left'): string | null => {
    if (!ankleData) return null;
    
    const prefix = side === 'right' ? 'right_' : 'left_';
    const plantarFlexion = ankleData[`${prefix}plantar_flexion`];
    const dorsiflexion = ankleData[`${prefix}dorsiflexion`];
    const inversion = ankleData[`${prefix}inversion`];
    const eversion = ankleData[`${prefix}eversion`];
    
    // Check if there's any data for this side
    if (plantarFlexion === null && dorsiflexion === null && inversion === null && eversion === null) {
      return null;
    }
    
    const plantarFlexionImpairment = calculateAnklePlantarFlexion(plantarFlexion);
    const dorsiflexionImpairment = calculateAnkleDorsiflexion(dorsiflexion);
    const inversionImpairment = calculateAnkleInversion(inversion);
    const eversionImpairment = calculateAnkleEversion(eversion);
    
    const motionImpairmentSum = plantarFlexionImpairment + dorsiflexionImpairment + inversionImpairment + eversionImpairment;
    const wholePersonImpairment = Math.round(motionImpairmentSum * 0.4);
    
    const capitalizedSide = side.charAt(0).toUpperCase() + side.slice(1) + ' ankle';
    
    if (motionImpairmentSum === 0) {
      return `<b><u>${capitalizedSide.toUpperCase()}:</u></b> No impairment based on range of motion.`;
    }
    
    const intro = `<b><u>${capitalizedSide.toUpperCase()}:</u></b>\n\nImpairment rating is based on range of motion deficit.`;
    
    const motionBreakdown = `Motion:\n` +
      `- Plantar Flexion ${plantarFlexion}° = ${plantarFlexionImpairment}% Lower Extremity Impairment [Table 17-11, p537]\n` +
      `- Dorsiflexion ${dorsiflexion}° = ${dorsiflexionImpairment}% Lower Extremity Impairment [Table 17-11, p537]\n` +
      `- Inversion ${inversion}° = ${inversionImpairment}% Lower Extremity Impairment [Table 17-12, p537]\n` +
      `- Eversion ${eversion}° = ${eversionImpairment}% Lower Extremity Impairment [Table 17-12, p537]`;

    const motionTotal = `→ Total motion impairment = ${plantarFlexionImpairment}% + ${dorsiflexionImpairment}% + ${inversionImpairment}% + ${eversionImpairment}% = ${motionImpairmentSum}% Lower Extremity Impairment.`;
    
    const totals = `Totals:\n` +
      `- Final Lower Extremity Impairment = ${motionImpairmentSum}%\n` +
      `→ Whole Person Impairment = ${motionImpairmentSum}% × 0.4 = ${wholePersonImpairment}% WPI [Table 17-3, p527].\n\nThis level of impairment is felt to most accurately reflect the residual effects of the work injury and takes into account the Almaraz/Guzman II decision.`;

    return [intro, motionBreakdown, motionTotal, totals].join('\n\n');
  };

  // Elbow calculation functions - exact n8n logic




  // Generate elbow narrative with exact n8n logic
  const generateElbowNarrative = (elbowData: any, side: 'right' | 'left'): string | null => {
    if (!elbowData) return null;
    
    const prefix = side === 'right' ? 'right_' : 'left_';
    const flexion = elbowData[`${prefix}flexion`];
    const extension = elbowData[`${prefix}extension`];
    const pronation = elbowData[`${prefix}pronation`];
    const supination = elbowData[`${prefix}supination`];
    
    // Check if there's any data for this side
    if (flexion === null && extension === null && pronation === null && supination === null) {
      return null;
    }
    
    // Calculate impairments
    const flexionImpairment = calculateElbowFlexion(flexion);
    const extensionImpairment = calculateElbowExtension(extension);
    const pronationImpairment = calculatePronation(pronation);
    const supinationImpairment = calculateSupination(supination);
    
    const motionImpairmentSum = flexionImpairment + extensionImpairment + pronationImpairment + supinationImpairment;
    const wholePersonImpairment = Math.round(motionImpairmentSum * 0.6);
    
    // Build the report conditionally - exact n8n structure
    const capitalizedSide = side.charAt(0).toUpperCase() + side.slice(1) + ' elbow';
    
    if (motionImpairmentSum === 0) {
      return `<b><u>${capitalizedSide.toUpperCase()}:</u></b> No impairment based on range of motion.`;
    }
    
    const intro = `<b><u>${capitalizedSide.toUpperCase()}:</u></b>\n\nImpairment rating is based on range of motion deficit. When measurements fall between standardized values, the corresponding impairment percentage is interpolated proportionally.`;
    
    const motionBreakdown = `Motion:\n` +
      `- Flexion ${flexion}° = ${flexionImpairment}% Upper Extremity Impairment [Figure 16-34, p472]\n` +
      `- Extension ${extension}° = ${extensionImpairment}% Upper Extremity Impairment [Figure 16-34, p472]\n` +
      `- Pronation ${pronation}° = ${pronationImpairment}% Upper Extremity Impairment [Figure 16-37, p474]\n` +
      `- Supination ${supination}° = ${supinationImpairment}% Upper Extremity Impairment [Figure 16-37, p474]`;

    const motionTotal = `→ Total motion impairment = ${flexionImpairment}% + ${extensionImpairment}% + ${pronationImpairment}% + ${supinationImpairment}% = ${motionImpairmentSum}% Upper Extremity Impairment.`;
    
    const totals = `Totals:\n` +
      `- Final Upper Extremity Impairment = ${motionImpairmentSum}%\n` +
      `→ Whole Person Impairment = ${motionImpairmentSum}% × 0.6 = ${wholePersonImpairment}% WPI [Table 16-3, p439].\n\nThis level of impairment is felt to most accurately reflect the residual effects of the work injury and takes into account the Almaraz/Guzman II decision.`;

    // Assemble the final narrative from the parts
    const narrative = [intro, motionBreakdown, motionTotal, totals].join('\n\n');
    
    return narrative;
  };
  
  // Grip strength calculation function copied exactly from App.tsx
  const calculateGripStrengthImpairment = (gripData: any, side: 'right' | 'left'): number => {
    // Return 0 if no grip data
    if (!gripData) return 0;
    
    // Map attempts based on tested vs non-tested side
    let testedAttempts: (number | null)[] = [];
    let nonTestedAttempts: (number | null)[] = [];
    
    if (side === 'right') {
      testedAttempts = [
        gripData.right_attempt_1,
        gripData.right_attempt_2,
        gripData.right_attempt_3
      ];
      nonTestedAttempts = [
        gripData.left_attempt_1,
        gripData.left_attempt_2,
        gripData.left_attempt_3
      ];
    } else { // side === 'left'
      testedAttempts = [
        gripData.left_attempt_1,
        gripData.left_attempt_2,
        gripData.left_attempt_3
      ];
      nonTestedAttempts = [
        gripData.right_attempt_1,
        gripData.right_attempt_2,
        gripData.right_attempt_3
      ];
    }
    
    // Helper function to calculate average, filtering out null/undefined values
    const safeAverage = (arr: (number | null)[]) => {
      const valid = arr.filter(val => val !== null && val !== undefined) as number[];
      if (valid.length === 0) return 0;
      return valid.reduce((sum, val) => sum + val, 0) / valid.length;
    };
    
    // Calculate averages
    const avgTested = safeAverage(testedAttempts);
    const avgNonTested = safeAverage(nonTestedAttempts);
    
    // Calculate decrease percentage
    const decreasePercent = avgNonTested > 0 ? Math.round((avgNonTested - avgTested) / avgNonTested * 100) : 0;
    
    // Rating based on decrease percentage according to Table 16-34, page 509 of AMA Guides
    // 10-30% loss = 10% UE
    // 31-60% loss = 20% UE
    // 61-100% loss = 30% UE
    let rating = 0;
    if (decreasePercent >= 61) rating = 30;
    else if (decreasePercent >= 31) rating = 20;
    else if (decreasePercent >= 10) rating = 10;
    
    return rating;
  };
  
  // Function to generate grip narrative
  const generateGripNarrative = (gripData: any, side: 'right' | 'left'): string | null => {
    if (!gripData) return null;
    
    // Get relevant grip strength values based on side
    const attempt1 = side === 'right' ? gripData.right_attempt_1 : gripData.left_attempt_1;
    const attempt2 = side === 'right' ? gripData.right_attempt_2 : gripData.left_attempt_2;
    const attempt3 = side === 'right' ? gripData.right_attempt_3 : gripData.left_attempt_3;
    
    // Get the non-tested side values
    const nonTestedAttempt1 = side === 'right' ? gripData.left_attempt_1 : gripData.right_attempt_1;
    const nonTestedAttempt2 = side === 'right' ? gripData.left_attempt_2 : gripData.right_attempt_2;
    const nonTestedAttempt3 = side === 'right' ? gripData.left_attempt_3 : gripData.right_attempt_3;
    
    // Helper function to safely calculate average of non-null grip values
    const calculateAverage = (values: (number | null)[]) => {
      const validValues = values.filter(val => val !== null && val !== undefined) as number[];
      if (validValues.length === 0) return 0;
      return validValues.reduce((sum, val) => sum + val, 0) / validValues.length;
    };
    
    // Calculate averages
    const testedAverage = calculateAverage([attempt1, attempt2, attempt3]);
    const nonTestedAverage = calculateAverage([nonTestedAttempt1, nonTestedAttempt2, nonTestedAttempt3]);
    
    // Calculate percentage decrease
    const decreasePercent = nonTestedAverage > 0 ? 
      Math.round((nonTestedAverage - testedAverage) / nonTestedAverage * 100) : 0;
    
    // Calculate UE impairment based on the mapping provided:
    // 10-30% loss = 10% UE
    // 31-60% loss = 20% UE
    // 61-100% loss = 30% UE
    const ueImpairment = calculateGripStrengthImpairment(gripData, side);
    
    // Convert UE to WPI based on the mapping provided:
    // 10% UE = 6% WPI
    // 20% UE = 12% WPI
    // 30% UE = 18% WPI
    let wpiImpairment = 0;
    if (ueImpairment === 10) wpiImpairment = 6;
    else if (ueImpairment === 20) wpiImpairment = 12;
    else if (ueImpairment === 30) wpiImpairment = 18;
    
    // Build narrative
    const gripTitle = side.charAt(0).toUpperCase() + side.slice(1) + ' Grip Strength';
    
    // If all attempts are null/undefined, return null
    if ((attempt1 === null || attempt1 === undefined) && 
        (attempt2 === null || attempt2 === undefined) && 
        (attempt3 === null || attempt3 === undefined)) {
      return null;
    }
    
    // Check if there's any actual impairment from calculations
    const hasImpairment = ueImpairment > 0;
    
    if (!hasImpairment) {
      return `${gripTitle}:\nNo impairment based on grip strength.`;
    }
    
    // New narrative format as provided by the user
    return `${gripTitle}:\n\nThis is one of the rare instances where the impairment would best be rated using loss of strength. \n\nIn this circumstance, none of the criteria that would prevent use of grip as a rating feature are present, those listed in Section 16.8a on page 508: a loss of motion, a painful condition, deformity, or an absence of parts.\n\nAdditionally, it has been at least 1 year from injury. Using the technique outlined in Section 16.8b on page 508, there was no more than 20% difference in the ratings and the test was repeated a total of three times.\n\nUsing Table 16-34 on page 509, there is a ${decreasePercent}% loss of strength, which equates to ${ueImpairment}% upper extremity impairment. Per Table 16-3, page 439, ${ueImpairment}% upper extremity impairment converts to ${wpiImpairment}% WPI. This level of impairment is felt to most accurately reflect the residual effects of the work injury and takes into account the Almaraz/Guzman II decision.`;
  };
  

  

  // Determine which narratives to generate based on the exam data
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const generateAllNarratives = useMemo(() => {
    const narratives: string[] = [];
    
    if (examData) {
      // Generate shoulder narratives
      if (examData.shoulders && hasShoulderInputs(examData.shoulders)) {
        const rightShoulderNarrative = generateShoulderNarrative(examData.shoulders, 'right');
        if (rightShoulderNarrative) {
          narratives.push(rightShoulderNarrative);
        }
        
        const leftShoulderNarrative = generateShoulderNarrative(examData.shoulders, 'left');
        if (leftShoulderNarrative) {
          narratives.push(leftShoulderNarrative);
        }
      }
      
      // Generate elbow narratives
      if (examData.elbows && hasElbowInputs(examData.elbows)) {
        const rightElbowNarrative = generateElbowNarrative(examData.elbows, 'right');
        if (rightElbowNarrative) {
          narratives.push(rightElbowNarrative);
        }
        
        const leftElbowNarrative = generateElbowNarrative(examData.elbows, 'left');
        if (leftElbowNarrative) {
          narratives.push(leftElbowNarrative);
        }
      }
      
      // Generate wrist narratives
      if (examData.wrists && hasWristInputs(examData)) {
        const rightWristNarrative = generateWristNarrative(examData.wrists, 'right');
        if (rightWristNarrative) {
          narratives.push(rightWristNarrative);
        }
        
        const leftWristNarrative = generateWristNarrative(examData.wrists, 'left');
        if (leftWristNarrative) {
          narratives.push(leftWristNarrative);
        }
      }
      
      // Generate hand narratives if hand data exists
      // Check for finger data in the correct nested structure from initializeExamData
      const handData = examData.hands || {
        thumbs: examData.thumbs,
        index_fingers: examData.index_fingers,
        middle_fingers: examData.middle_fingers,
        ring_fingers: examData.ring_fingers,
        little_fingers: examData.small_fingers // Note: backend returns 'small_fingers', frontend expects 'little_fingers'
      };
      
      // Only check for hand inputs if we have actual data (not just null placeholders)
      const hasValidHandData = handData && Object.keys(handData).some(fingerKey => {
        const fingerData = handData[fingerKey];
        return fingerData && Object.keys(fingerData).some(key => {
          const val = fingerData[key];
          return val !== null && val !== undefined && val !== '' && 
                 ((typeof val === 'number' && !isNaN(val)) || typeof val === 'string');
        });
      });
      
      if (hasValidHandData && hasHandInputs(handData)) {
        
        // Generate right hand narrative
        const rightHandNarrative = generateHandNarrative(handData, 'right');
        if (rightHandNarrative) narratives.push(rightHandNarrative);
        
        // Generate left hand narrative
        const leftHandNarrative = generateHandNarrative(handData, 'left');
        if (leftHandNarrative) narratives.push(leftHandNarrative);
        
      }
      
      // Generate grip strength narratives
      if (examData.grip_strength?.limited_by) {
        narratives.push(`Grip Strength:\n${examData.grip_strength.limited_by === 'pain' ? GRIP_PAIN_NOTE : GRIP_EFFORT_NOTE}`);
      } else if (examData.grip_strength && hasGripInputs(examData.grip_strength)) {
        // Determine which side has the lower grip strength (should get the impairment)
        const rightAttempts = [
          examData.grip_strength.right_attempt_1,
          examData.grip_strength.right_attempt_2,
          examData.grip_strength.right_attempt_3
        ];
        
        const leftAttempts = [
          examData.grip_strength.left_attempt_1,
          examData.grip_strength.left_attempt_2,
          examData.grip_strength.left_attempt_3
        ];
        
        // Calculate averages for comparison
        const calcAvg = (values: (number | null | undefined)[]): number => {
          const validValues = values.filter(val => val !== null && val !== undefined) as number[];
          if (validValues.length === 0) return 0;
          return validValues.reduce((sum, val) => sum + val, 0) / validValues.length;
        };
        
        const rightAverage = calcAvg(rightAttempts);
        const leftAverage = calcAvg(leftAttempts);

        // Grip is rated by comparing a side against its contralateral side, so only
        // the WEAKER side can carry impairment — rating both would just append a
        // "No impairment" paragraph for the strong side. When only one side was
        // tested, rate that one.
        const rightTested = rightAttempts.some(v => v !== null && v !== undefined);
        const leftTested = leftAttempts.some(v => v !== null && v !== undefined);
        let weakerSide: 'right' | 'left' | null = null;
        if (rightTested && leftTested) weakerSide = rightAverage <= leftAverage ? 'right' : 'left';
        else if (rightTested) weakerSide = 'right';
        else if (leftTested) weakerSide = 'left';

        if (weakerSide) {
          const gripNarrative = generateGripNarrative(examData.grip_strength, weakerSide);
          if (gripNarrative) narratives.push(gripNarrative);
        }
      }
      
      // Generate hip narratives. A hip marked as replaced (THA) is rated by the
      // Table 17-34 point score INSTEAD of its range of motion (never both).
      if (examData.hips) {
        const hips: any = examData.hips;
        for (const side of ['right', 'left'] as const) {
          const tha = replacementRating(THA, hips, side);
          if (tha) {
            narratives.push(tha.narrative);
          } else if (hasHipInputs(hips)) {
            const hipNarrative = generateHipNarrative(hips, side);
            if (hipNarrative) narratives.push(hipNarrative);
          }
        }
      }
      
      // Generate knee narratives. A knee marked as replaced (TKA) is rated by the
      // Table 17-35 point score INSTEAD of its range of motion (never both).
      if (examData.knees) {
        const knees: any = examData.knees;
        for (const side of ['right', 'left'] as const) {
          const tka = replacementRating(TKA, knees, side);
          if (tka) {
            narratives.push(tka.narrative);
          } else if (hasKneeInputs(knees)) {
            const kneeNarrative = generateKneeNarrative(knees, side);
            if (kneeNarrative) narratives.push(kneeNarrative);
          }
        }
      }
      
      // Generate ankle narratives
      if (examData.ankles && hasAnkleInputs(examData.ankles)) {
        const rightAnkleNarrative = generateAnkleNarrative(examData.ankles, 'right');
        if (rightAnkleNarrative) {
          narratives.push(rightAnkleNarrative);
        }
        
        const leftAnkleNarrative = generateAnkleNarrative(examData.ankles, 'left');
        if (leftAnkleNarrative) {
          narratives.push(leftAnkleNarrative);
        }
      }
      
    }
    
    // Never let a template print an unrecorded value ("nullº", "null cm").
    return narratives.map(omitUnrecorded);
  }, [examData]);

  const [excludedIds, setExcludedIds] = useState<string[]>([]);
  const parts = useMemo(() => ratedParts(generateAllNarratives), [generateAllNarratives]);
  const total = combinedTotal(parts, parts.filter(p => !excludedIds.includes(p.id)).map(p => p.id));
  const combinedNarrative = combinedTotalNarrative(total);
  const reportNarratives = useMemo(() => combinedNarrative
    ? [...generateAllNarratives, combinedNarrative] : generateAllNarratives,
    [generateAllNarratives, combinedNarrative]);
  const togglePart = (id: string) => setExcludedIds(previous => previous.includes(id)
    ? previous.filter(value => value !== id) : [...previous, id]);

  // Copy the report the way QME - Tool does: one clipboard item carrying BOTH
  // text/html (Times New Roman, keeping the narratives' bold/underline so it
  // pastes into Word formatted) and text/plain. The previous implementation
  // selected the hidden textarea and ran document.execCommand('copy'), which
  // delivered unstyled plain text despite advertising Times New Roman.
  const handleCopy = useCallback(async () => {
    const ok = await copyReportToClipboard(reportNarratives);
    if (ok) {
      setCopyFailed(false);
      setCopied(true);
      setTimeout(() => setCopied(false), 1800);
    } else {
      // Last resort: select the hidden textarea so a manual Cmd+C still works.
      const ta = document.getElementById('hidden-copy-area') as HTMLTextAreaElement | null;
      ta?.select();
      setCopyFailed(true);
    }
  }, [reportNarratives]);


  // Group narratives by body region
  const groupedNarratives = useMemo(() => {
    const groups: {[key: string]: string[]} = {};
    
    generateAllNarratives.forEach(narrative => {
      let region = 'Other';
      
      if (narrative.includes('Hand Impairment') || narrative.includes('Grip Strength')) {
        region = 'Hand & Grip';
      } else if (narrative.includes('Shoulder')) {
        region = 'Shoulder';
      } else if (narrative.includes('Elbow')) {
        region = 'Elbow';
      } else if (narrative.includes('Wrist')) {
        region = 'Wrist';
      } else if (narrative.includes('Hip')) {
        region = 'Hip';
      } else if (narrative.includes('Knee')) {
        region = 'Knee';
      } else if (narrative.includes('Ankle')) {
        region = 'Ankle';
      }
      
      if (!groups[region]) {
        groups[region] = [];
      }
      groups[region].push(narrative);
    });
    
    return groups;
  }, [generateAllNarratives]);
  


  // Wrap AMA citations like [Table 16-3, p439] in teal-mono spans. Display only —
  // the clipboard is built from the RAW narratives, so this styling never travels
  // with a copy (the pasted report keeps only the narratives' own bold/underline).
  const styleCitations = (html: string) =>
    html.replace(/\[[^\]\n]+\]/g, '<span class="ama-cite">$&</span>');

  return (
    <div
      id="results-summary-box"
      className="card overflow-hidden mb-5 relative"
      style={{ fontFamily: "'Times New Roman', Times, serif", fontSize: '12pt' }}
    >
      {/* Header with title and controls */}
      <div className="flex justify-between items-center flex-wrap gap-2.5 px-4 py-3 border-b border-border font-sans">
        <h2 className="text-xl font-bold text-content m-0">Results Summary</h2>

        <div className="flex gap-2.5 items-center">
          <button
            onClick={handleCopy}
            className="btn-primary whitespace-nowrap font-sans disabled:opacity-40 disabled:cursor-not-allowed"
            disabled={generateAllNarratives.length === 0}
            aria-label="Copy the rating report to the clipboard"
          >
            {copied ? 'Copied' : copyFailed ? 'Press Cmd+C' : 'Copy report'}
          </button>
        </div>
      </div>
      

      {/* On-screen only (never copied): abnormal two-point values the rating
          can't use because no digit-length % was recorded. */}
      {unratedSensory(examData).length > 0 && (
        <div role="alert" className="mx-4 mt-3 rounded border border-amber-500/60 bg-amber-500/10 px-3 py-2 text-sm font-sans text-content">
          <strong>Sensory loss not rated:</strong> {unratedSensory(examData).join('; ')}.{' '}
          Enter the percent of digit length involved on the Hands tab to rate it.
        </div>
      )}
      {gripMotionConflict(generateAllNarratives).length > 0 && (
        <div role="alert" className="mx-4 mt-3 rounded border border-amber-500/60 bg-amber-500/10 px-3 py-2 text-sm font-sans text-content">
          <strong>Grip conflicts with motion loss:</strong> the grip paragraph says there is no loss of motion
          (§16.8a), but this report also rates {gripMotionConflict(generateAllNarratives).join(', ')}. Grip is
          normally not used alongside a motion rating in the same arm; remove one before pasting.
        </div>
      )}
      {incompleteGrip(examData).length > 0 && (
        <div role="alert" className="mx-4 mt-3 rounded border border-amber-500/60 bg-amber-500/10 px-3 py-2 text-sm font-sans text-content">
          <strong>Check the grip text before pasting:</strong> {incompleteGrip(examData).join('; ')}. The
          report says the test was repeated three times with no more than 20% difference.
        </div>
      )}

      {parts.some(p => /Grip Strength/i.test(p.label) && (p.wpi ?? 0) > 0) && (
        <div role="alert" className="mx-4 mt-3 rounded border border-amber-500/60 bg-amber-500/10 px-3 py-2 text-sm font-sans text-content">
          The grip paragraph states it has been at least 1 year from injury. Confirm that before pasting.
        </div>
      )}

      {/* At a glance: the final WPI of each rated side, read from the report
          text itself so it can never disagree with what gets pasted. */}
      {(ratingSummary(generateAllNarratives).length > 0 || parts.length > 0) && (
        <div className="px-4 pt-4 font-sans" aria-label="Ratings at a glance">
          <ul className="flex flex-wrap gap-2">
            {ratingSummary(generateAllNarratives).map((r) => (
              <li key={r.label} className={`flex items-baseline gap-2 rounded-lg border px-3 py-1.5 ${r.wpi > 0 ? 'border-accent-500/40 bg-accent-500/10' : 'border-border'}`}>
                {parts.filter(p => p.label === r.label).map(p => (
                  <input key={p.id} type="checkbox" checked={!excludedIds.includes(p.id)}
                    onChange={() => togglePart(p.id)}
                    aria-label={`${p.label} included in the combined total`}
                    className="accent-accent-500" />
                ))}
                <span className="text-sm text-content-secondary">{r.label}</span>
                <span className={`text-base font-semibold tabular-nums ${r.wpi > 0 ? 'text-content' : 'text-content-tertiary'}`}>{r.wpi}% WPI</span>
              </li>
            ))}
          </ul>
          {parts.filter(p => !ratingSummary(generateAllNarratives).some(r => r.label === p.label)).map(p => (
            <label key={p.id} className="mt-2 flex items-center gap-2 text-sm text-content-secondary">
              <input type="checkbox" checked={!excludedIds.includes(p.id)} onChange={() => togglePart(p.id)}
                aria-label={`${p.label} included in the combined total`} />{p.label}
            </label>
          ))}
          {total.selected.length >= 2 && (
            <div className="mt-3 rounded-lg border border-accent-500/40 bg-accent-500/10 p-3 text-sm text-content" aria-live="polite">
              <p className="font-semibold">{total.unavailable ? 'Combined WPI unavailable' : `Combined WPI: ${total.wpi}%`}</p>
              {total.unavailable && <p>{total.unavailable}</p>}
              {total.steps.map(step => <p key={step} className="mt-1">{step}</p>)}
            </div>
          )}
        </div>
      )}

      {/* Full report, at full length. It used to sit in a 600px scroll box
          inside the scrolling page, so a ~2,800px report was read through a slot. */}
      <div className="p-4">
        {Object.keys(groupedNarratives).length > 0 ? (
          Object.entries(groupedNarratives).map(([region, narratives], regionIndex) => {
            return (
              <div key={region} className={
                regionIndex < Object.keys(groupedNarratives).length - 1
                  ? 'mb-8 pb-5 border-b-2 border-border'
                  : ''
              }>
                {/* Content - always visible */}
                <div>
                  {narratives.map((narrative, index) => {
                    // Check if this is a left-side narrative that immediately follows a right-side narrative of the same joint
                    const isLeftFollowingRight = index > 0 && (
                      (narrative.startsWith('Left Shoulder') && narratives[index - 1].startsWith('Right Shoulder')) ||
                      (narrative.startsWith('Left Elbow') && narratives[index - 1].startsWith('Right Elbow')) ||
                      (narrative.startsWith('Left Wrist') && narratives[index - 1].startsWith('Right Wrist')) ||
                      (narrative.startsWith('Left Hip') && narratives[index - 1].startsWith('Right Hip')) ||
                      (narrative.startsWith('Left Knee') && narratives[index - 1].startsWith('Right Knee')) ||
                      (narrative.startsWith('Left Ankle') && narratives[index - 1].startsWith('Right Ankle')) ||
                      (narrative.startsWith('Left Grip Strength') && narratives[index - 1].startsWith('Right Grip Strength')) ||
                      (narrative.startsWith('Left Hand Impairment') && narratives[index - 1].startsWith('Right Hand Impairment'))
                    );
                    
                    // Check if this is a right-side narrative that should have a line before the left-side narrative
                    const isBetweenSameJoint = ((narrative.startsWith('Right Shoulder') && index < narratives.length - 1 && 
                      narratives[index + 1].startsWith('Left Shoulder')) ||
                     (narrative.startsWith('Right Elbow') && index < narratives.length - 1 && 
                      narratives[index + 1].startsWith('Left Elbow')) ||
                     (narrative.startsWith('Right Wrist') && index < narratives.length - 1 && 
                      narratives[index + 1].startsWith('Left Wrist')) ||
                     (narrative.startsWith('Right Hip') && index < narratives.length - 1 && 
                      narratives[index + 1].startsWith('Left Hip')) ||
                     (narrative.startsWith('Right Knee') && index < narratives.length - 1 && 
                      narratives[index + 1].startsWith('Left Knee')) ||
                     (narrative.startsWith('Right Ankle') && index < narratives.length - 1 && 
                      narratives[index + 1].startsWith('Left Ankle')) ||
                     (narrative.startsWith('Right Grip Strength') && index < narratives.length - 1 &&
                     narratives[index + 1].startsWith('Left Grip Strength')) ||
                     (narrative.startsWith('Right Hand Impairment') && index < narratives.length - 1 &&
                     narratives[index + 1].startsWith('Left Hand Impairment')));
                    
                    return (
                      <React.Fragment key={index}>
                        {index > 0 && !isLeftFollowingRight && (
                          <hr className="my-5 border-none h-px bg-content-tertiary/40 shadow-sm" />
                        )}
                        <p
                          className="my-1 whitespace-pre-line"
                          dangerouslySetInnerHTML={{ __html: styleCitations(narrative) }}  // narratives are generated internally; citations styled for display
                        />
                        {isBetweenSameJoint && (
                          <hr className="my-4 border-none h-px bg-border" />
                        )}
                      </React.Fragment>
                    );
                  })}
                </div>
              </div>
            );
          })
        ) : (
          <div className="flex flex-col items-center justify-center py-12 text-center font-sans">
            <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" className="text-content-tertiary/50 mb-4">
              <path strokeLinecap="round" strokeLinejoin="round" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
            </svg>
            <p className="text-content-tertiary text-sm mb-1">No impairment data available</p>
            <p className="text-content-tertiary/60 text-xs">Enter exam data in the tabs above to generate results</p>
          </div>
        )}
      </div>
      
      <textarea
        id="hidden-copy-area"
        readOnly
        // Join narratives, then collapse runs of 2+ newlines to a single newline
        // so the copied text pastes single-spaced (no blank lines between paragraphs)
        value={reportNarratives.map(narrative => stripHtmlTags(narrative)).join('\n').replace(/\n{2,}/g, '\n')}
        className="absolute -left-[9999px] top-0 h-px w-px"
      />
    </div>
  );
};

export default ResultsSummaryBox;
